import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from './contexts/LanguageContext';
import './KnowledgeBasePage.css';
import knowledgeData from './data/knowledgeBase.json';

const KnowledgeBasePage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  
  // 知识库状态
  const [knowledgeList, setKnowledgeList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('全部');
  const [selectedMemoryLevel, setSelectedMemoryLevel] = useState('全部');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // 检查用户登录状态
  useEffect(() => {
    const storedUser = localStorage.getItem('talktastic_user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        if (user && user.isRegistered) {
          setIsLoggedIn(true);
          setUserData(user);
        }
      } catch (error) {
        console.error('读取用户信息失败:', error);
      }
    }
  }, []);

  // 加载知识库
  useEffect(() => {
    setKnowledgeList(knowledgeData.knowledgeBase);
    setFilteredList(knowledgeData.knowledgeBase);
  }, []);

  // 筛选逻辑
  useEffect(() => {
    let filtered = knowledgeList;

    // 搜索过滤
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.meaning.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // 分类过滤
    if (selectedCategory !== '全部') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    // 记忆程度过滤
    if (selectedMemoryLevel !== '全部') {
      filtered = filtered.filter(item => item.memoryLevel === selectedMemoryLevel);
    }

    setFilteredList(filtered);
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, selectedMemoryLevel, knowledgeList]);

  // 分页逻辑
  const totalPages = Math.ceil(filteredList.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredList.slice(startIndex, startIndex + itemsPerPage);

  // 记忆程度徽章颜色
  const getMemoryLevelColor = (level) => {
    const colors = {
      '初见': '#ff6b6b',
      '不熟': '#ffa500',
      '老友': '#4ecdc4',
      '挚友': '#95e1d3'
    };
    return colors[level] || '#ccc';
  };

  // 导航回主页
  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <div className="knowledge-base-container">
      {/* 主要内容区域 */}
      <div className="knowledge-content">
        {/* 知识库标题 */}
        <div className="knowledge-header">
          <h1>{language === 'en' ? 'My Knowledge Base' : '我的知识库'}</h1>
          <div className="knowledge-stats">
            <span className="stat-item">
              📚 总计: {knowledgeData.total}条
            </span>
            <span className="stat-item">
              🔥 初见: {knowledgeData.memoryLevels['初见']}
            </span>
            <span className="stat-item">
              📖 不熟: {knowledgeData.memoryLevels['不熟']}
            </span>
            <span className="stat-item">
              💪 老友: {knowledgeData.memoryLevels['老友']}
            </span>
            <span className="stat-item">
              ⭐ 挚友: {knowledgeData.memoryLevels['挚友']}
            </span>
          </div>
        </div>

        {/* 筛选工具栏 */}
        <div className="knowledge-toolbar">
          <div className="search-box">
            <input
              type="text"
              placeholder={language === 'en' ? 'Search...' : '搜索知识点或意思...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="filter-box">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="filter-select"
            >
              <option value="全部">全部分类</option>
              {knowledgeData.categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <select
              value={selectedMemoryLevel}
              onChange={(e) => setSelectedMemoryLevel(e.target.value)}
              className="filter-select"
            >
              <option value="全部">全部记忆程度</option>
              <option value="初见">初见</option>
              <option value="不熟">不熟</option>
              <option value="老友">老友</option>
              <option value="挚友">挚友</option>
            </select>
          </div>
        </div>

        {/* 知识库表格 */}
        <div className="knowledge-table-container">
          <table className="knowledge-table">
            <thead>
              <tr>
                <th style={{width: '25%'}}>知识点</th>
                <th style={{width: '25%'}}>意思</th>
                <th style={{width: '12%'}}>分类</th>
                <th style={{width: '12%'}}>记忆程度</th>
                <th style={{width: '10%'}}>应用次数</th>
                <th style={{width: '16%'}}>导入日期</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length > 0 ? (
                currentItems.map((item) => (
                  <tr key={item.id}>
                    <td className="knowledge-content-cell">
                      <strong>{item.content}</strong>
                    </td>
                    <td className="knowledge-meaning-cell">
                      {item.meaning}
                    </td>
                    <td className="knowledge-category-cell">
                      <span className="category-badge">{item.category}</span>
                    </td>
                    <td className="knowledge-memory-cell">
                      <span 
                        className="memory-badge"
                        style={{backgroundColor: getMemoryLevelColor(item.memoryLevel)}}
                      >
                        {item.memoryLevel}
                      </span>
                    </td>
                    <td className="knowledge-usage-cell">
                      {item.usageCount}次
                    </td>
                    <td className="knowledge-date-cell">
                      {typeof item.importDate === 'string' ? item.importDate : 
                       typeof item.importDate === 'number' && item.importDate > 40000 ?
                         new Date((item.importDate - 25569) * 86400000).toISOString().slice(0, 10) :
                         item.importDate}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{textAlign: 'center', padding: '2rem', color: '#999'}}>
                    {language === 'en' ? 'No matching items found' : '未找到匹配的知识点'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 分页控制 */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              上一页
            </button>
            <span className="pagination-info">
              第 {currentPage} 页 / 共 {totalPages} 页 ({filteredList.length}条)
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              下一页
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeBasePage; 